import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertCircle, Loader2, Trash2, Unlink, X } from 'lucide-react';

export function DeleteRoleTemplateDialog({ isOpen, onClose, template, onSuccess }) {
  const [deleting, setDeleting] = useState(false);
  const [mode, setMode] = useState(null); // 'remove' | 'keep'

  if (!template) return null;

  const handleConfirm = async () => {
    if (mode === 'remove') {
      await handleRemoveRequirements();
    } else {
      await handleUnlinkRequirements();
    }
  };

  const handleRemoveRequirements = async () => {
    setDeleting(true);
    try {
      // 1. Find all assignments where this template is referenced
      const { data: assignments, error: fetchError } = await supabase
        .from('compliance_assignments')
        .select('id, role_template_ids')
        .contains('role_template_ids', [template.id]);

      if (fetchError) throw fetchError;

      // 2. Process each assignment
      for (const assignment of (assignments || [])) {
        const templateIds = assignment.role_template_ids || [];
        const remaining = templateIds.filter(id => id !== template.id);

        if (remaining.length === 0) {
          // This template was the only source - unassign the requirement
          await supabase
            .from('compliance_assignments')
            .update({
              assigned: false,
              role_template_ids: [],
              updated_date: new Date().toISOString()
            })
            .eq('id', assignment.id);
        } else {
          // Other templates still reference this - just remove this template ID
          await supabase
            .from('compliance_assignments')
            .update({
              role_template_ids: remaining,
              updated_date: new Date().toISOString()
            })
            .eq('id', assignment.id);
        }
      }

      // 3. Delete user-to-template links
      await supabase
        .from('user_role_templates')
        .delete()
        .eq('role_template_id', template.id);

      // 4. Delete role_template_requirements
      await supabase
        .from('role_template_requirements')
        .delete()
        .eq('role_template_id', template.id);

      // 5. Delete role template
      const { error: deleteError } = await supabase
        .from('role_templates')
        .delete()
        .eq('id', template.id);

      if (deleteError) throw deleteError;

      toast.success('Role template deleted and requirements removed');
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Error deleting role template with requirements:', err);
      toast.error('Failed to delete role template');
    } finally {
      setDeleting(false);
    }
  };

  const handleUnlinkRequirements = async () => {
    setDeleting(true);
    try {
      // 1. Find all assignments where this template is referenced
      const { data: assignments, error: fetchError } = await supabase
        .from('compliance_assignments')
        .select('id, role_template_ids')
        .contains('role_template_ids', [template.id]);

      if (fetchError) throw fetchError;

      // 2. Remove this template ID from all assignments (keep assignments in place)
      for (const assignment of (assignments || [])) {
        const templateIds = assignment.role_template_ids || [];
        const remaining = templateIds.filter(id => id !== template.id);

        await supabase
          .from('compliance_assignments')
          .update({
            role_template_ids: remaining,
            updated_date: new Date().toISOString()
          })
          .eq('id', assignment.id);
      }

      // 3. Delete user-to-template links
      await supabase
        .from('user_role_templates')
        .delete()
        .eq('role_template_id', template.id);

      // 4. Delete role_template_requirements
      await supabase
        .from('role_template_requirements')
        .delete()
        .eq('role_template_id', template.id);

      // 5. Delete role template
      const { error: deleteError } = await supabase
        .from('role_templates')
        .delete()
        .eq('id', template.id);

      if (deleteError) throw deleteError;

      toast.success('Role template deleted (requirements kept as manual)');
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Error unlinking role template:', err);
      toast.error('Failed to delete role template');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open && !deleting) { setMode(null); onClose(); } }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="space-y-3">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
            <Trash2 className="h-6 w-6 text-red-600" />
          </div>
          <DialogTitle className="text-center">Delete Role Template</DialogTitle>
          <DialogDescription className="text-center">
            Are you sure you want to delete <strong>{template.name}</strong>?
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <AlertCircle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">
            This template has <strong>{template.requirementCount} requirements</strong> and is connected to <strong>{template.connectedUsers} users</strong>. Choose how to handle existing assignments.
          </p>
        </div>

        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700">How should existing assignments be handled?</p>

          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setMode('remove')}
              disabled={deleting}
              className={`flex flex-col items-center gap-2 rounded-lg border-2 p-4 text-center transition-colors disabled:opacity-50 ${
                mode === 'remove'
                  ? 'border-red-500 bg-red-50 ring-1 ring-red-500'
                  : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              <Trash2 className={`h-5 w-5 ${mode === 'remove' ? 'text-red-600' : 'text-gray-400'}`} />
              <div>
                <div className={`text-sm font-semibold ${mode === 'remove' ? 'text-red-700' : 'text-gray-700'}`}>Remove</div>
                <div className={`text-xs ${mode === 'remove' ? 'text-red-600/70' : 'text-muted-foreground'}`}>Unassign linked requirements</div>
              </div>
            </button>

            <button
              onClick={() => setMode('keep')}
              disabled={deleting}
              className={`flex flex-col items-center gap-2 rounded-lg border-2 p-4 text-center transition-colors disabled:opacity-50 ${
                mode === 'keep'
                  ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                  : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              <Unlink className={`h-5 w-5 ${mode === 'keep' ? 'text-blue-600' : 'text-gray-400'}`} />
              <div>
                <div className={`text-sm font-semibold ${mode === 'keep' ? 'text-blue-700' : 'text-gray-700'}`}>Keep</div>
                <div className={`text-xs ${mode === 'keep' ? 'text-blue-600/70' : 'text-muted-foreground'}`}>Keep assignments as manual</div>
              </div>
            </button>
          </div>
        </div>

        <div className="flex gap-3 justify-end">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            className="bg-red-600 hover:bg-red-700 text-white"
            onClick={handleConfirm}
            disabled={!mode || deleting}
          >
            {deleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
            Delete Template
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default DeleteRoleTemplateDialog;
